// src/Chat.js
import React, { useState, useEffect, useRef } from "react";
import Picker from "@emoji-mart/react";
import PropTypes from "prop-types";

const Chat = () => {
    const [messages, setMessages] = useState(
        () => JSON.parse(localStorage.getItem("chat_messages")) || []
    );
    const [message, setMessage] = useState("");
    const [socket, setSocket] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

    const messagesEndRef = useRef(null);
    const emojiPickerRef = useRef(null);
    const chatContainerRef = useRef(null);
    const inputRef = useRef(null);
    // Fix crypto.randomUUID()
    const [visitorId] = useState(() => {
        const storedId = localStorage.getItem("visitorId");
        if (storedId) return storedId;

        // Cross-browser compatible UUID generation
        let newId;
        if (crypto.randomUUID) {
            newId = crypto.randomUUID();
        } else {
            // Fallback for browsers that don't support randomUUID
            newId = ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
                (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
            );
        }

        localStorage.setItem("visitorId", newId);
        return newId;
    });

    //crypto.randomUUID()

    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const wsUrl = `${process.env.REACT_APP_API_URL.replace("https", "wss").replace("http", "ws")}/ws/chat/${visitorId}/`;
        const ws = new WebSocket(wsUrl);
        setSocket(ws);

        ws.onopen = () => {
            console.log("✅ WebSocket Connected for Visitor");
        };

        ws.onmessage = (e) => {
            try {
                const data = JSON.parse(e.data);
                console.log("📩 Received Message:", data);

                setMessages((prev) => {
                    // Check for duplicates with better criteria
                    const messageExists = prev.some((msg) =>
                        // Check if timestamp matches
                        (msg.timestamp === data.timestamp && msg.message === data.message) ||
                        // Or check if it's a message we sent (by clientId)
                        (data.clientId && msg.clientId === data.clientId) ||
                        // Or check by exact content and sender in case clientId isn't preserved
                        (msg.sender === data.sender &&
                            msg.message === data.message &&
                            Math.abs(new Date(msg.timestamp) - new Date(data.timestamp)) < 5000) // Within 5 seconds
                    );
                    if (!messageExists) {
                        const updatedMessages = [...prev, data];
                        localStorage.setItem("chat_messages", JSON.stringify(updatedMessages));
                        return updatedMessages;
                    }
                    return prev;
                });
            } catch (error) {
                console.error("❌ Error parsing WebSocket message:", error);
            }
        };

        ws.onerror = (error) => {
            console.error("❌ WebSocket Error:", error);
        };

        return () => ws.close();
    }, [visitorId]);

    useEffect(() => {
        if (isOpen) {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages, isOpen]);

    // ✅ Modified: Close chat when clicking outside, but with better event handling
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Only process this if the chat is open
            if (!isOpen) return;

            // Add a check to prevent interfering with form submissions
            if (event.target.tagName === 'BUTTON' ||
                event.target.closest('button') ||
                event.target.closest('form')) {
                return;
            }

            if (
                chatContainerRef.current &&
                !chatContainerRef.current.contains(event.target) &&
                !event.target.closest('.emoji-mart')
            ) {
                setShowEmojiPicker(false);
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    const sendMessage = (e) => {
        e.preventDefault();
        if (message.trim() && socket) {
            const messageData = {
                message: message.trim(),
                sender: "Visitor",
                room_id: visitorId,
                timestamp: new Date().toISOString(),
                clientId: `local-${Date.now()}` // Add this line to create a unique ID
            };

            try {
                socket.send(JSON.stringify(messageData));
                setMessages(prev => {
                    const updatedMessages = [...prev, messageData];
                    localStorage.setItem("chat_messages", JSON.stringify(updatedMessages));
                    return updatedMessages;
                });

                setMessage("");
                setShowEmojiPicker(false);

                // ✅ Keep input field focused after sending message
                inputRef.current?.focus();
            } catch (error) {
                console.error("❌ Error sending WebSocket message:", error);
            }
        }
    };

    const addEmoji = (emoji) => {
        setMessage((prev) => prev + emoji.native);
        inputRef.current?.focus(); // ✅ Prevent text loss after selecting emoji
    };

    const toggleChat = () => {
        setIsOpen((prev) => !prev);
        setShowEmojiPicker(false);
    };

    return (
        <div
            ref={chatContainerRef}
            // 1) Pin to bottom-0 for consistent alignment
            // 2) Use transform on this container to slide offscreen
            className={`
            fixed bottom-0
            ${isMobileView ? "right-2 w-[95%] max-w-md" : "right-4 w-80"}
            z-50 overflow-hidden
            transition-transform duration-300 ease-in-out
            ${isOpen ? "translate-y-0" : "translate-y-[calc(100%-3.5rem)]"}
          `}
        >
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                {/* Chat Header */}
                <div
                    className="p-4 border-b dark:border-gray-700 flex justify-between items-center cursor-pointer"
                    onClick={toggleChat}
                >
                    <h3 className="font-semibold text-gray-800 dark:text-white flex items-center">
                        <span className="mr-2">💬</span>
                        Chat with Me
                    </h3>
                </div>

                {/* Messages Area */}
                <div className="overflow-y-auto transition-all duration-300 h-96 p-4">
                    {messages.map((msg, index) => (
                        <div
                            key={`${msg.timestamp}-${index}`}
                            className={`mb-4 ${msg.sender === "Visitor" ? "text-right" : ""}`}
                        >
                            <div
                                className={`inline-block p-3 rounded-lg max-w-[85%] ${msg.sender === "Visitor"
                                    ? "bg-blue-500 text-white"
                                    : "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-white"
                                    }`}
                            >
                                <p className="text-sm whitespace-pre-wrap break-words">
                                    {msg.message}
                                </p>
                                <small className="text-xs opacity-75 mt-1 block">
                                    {msg.sender}
                                </small>
                            </div>
                        </div>
                    ))}
                    <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                {isOpen && (
                    <form
                        onSubmit={sendMessage}
                        className="p-4 border-t dark:border-gray-700 flex items-center gap-3 w-full"
                    >
                        {/* Emoji Button */}
                        <button
                            type="button"
                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0"
                        >
                            😀
                        </button>

                        {/* Emoji Picker Dropdown */}
                        {showEmojiPicker && (
                            <div
                                ref={emojiPickerRef}
                                className="absolute bottom-20 left-0 z-50"
                                style={{
                                    width: isMobileView ? '100%' : '320px'
                                }}
                            >
                                <Picker
                                    onEmojiSelect={addEmoji}
                                    // Minimal configuration - closer to original
                                    perLine={isMobileView ? 7 : 8}
                                />
                            </div>
                        )}

                        {/* Input Field */}
                        <input
                            ref={inputRef}
                            type="text"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            className="flex-1 p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                            placeholder="Type a message..."
                        />

                        {/* Send Button */}
                        <button
                            type="submit"
                            disabled={!message.trim()}
                            className="px-4 py-2 min-w-[80px] md:min-w-[90px] lg:min-w-[100px] w-auto rounded-lg bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-50 flex-shrink-0"
                        >
                            Send
                        </button>
                    </form>
                )}
            </div>
        </div>
    );

};

Chat.propTypes = {
    messages: PropTypes.array,
    setMessages: PropTypes.func,
    socket: PropTypes.object,
    visitorId: PropTypes.string,
};

export default Chat;